import * as React from "react";

import { Box, Button, Stack, Text, useMantineTheme } from "@mantine/core";
import { useElementSize, useMergedRef } from "@mantine/hooks";
import { IconPointFilled } from "@tabler/icons-react";
import _ from "lodash";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppState } from "../../hooks/useAppState";
import { useLibraryState } from "../../hooks/useEditorState";
import { librarySlice } from "../../lib/state/reducer";

const computeScrollRemaining = (
  el: React.RefObject<HTMLDivElement>
): { top: number; bottom: number } => {
  if (
    !el ||
    !el.current ||
    el.current.scrollHeight === undefined ||
    el.current.clientHeight === undefined ||
    el.current.scrollTop === undefined
  ) {
    return { top: 0, bottom: 0 };
  }

  const isScrollable = el.current.scrollHeight > el.current.clientHeight;
  if (!isScrollable) {
    return { top: 0, bottom: 0 };
  }

  return {
    top: el.current.scrollTop,
    bottom: el.current.scrollHeight - (el.current.clientHeight + el.current.scrollTop + 1),
  };
};

export const NavbarLibraryList = ({
  editorActive,
  onSelect,
}: {
  editorActive: boolean;
  onSelect: () => void;
}): JSX.Element | null => {
  const [scrollRemaining, setScrollRemaining] = React.useState<{ top: number; bottom: number }>({
    top: 0,
    bottom: 0,
  });

  const libraryIndexes = useAppState((state) => {
    const sorted = _.sortBy(state.library.entries, ["mtime"]).reverse();
    const indexes = sorted.map((e) =>
      _.findIndex(state.library.entries, (x) => x.present.id === e.present.id)
    );
    return indexes.join(",");
  });

  const fadeEffectRef = React.useRef<HTMLDivElement>(null);
  const { ref: resizeRef, height: containerHeight } = useElementSize();
  const containerRef = useMergedRef(fadeEffectRef, resizeRef);

  React.useLayoutEffect(() => {
    setScrollRemaining(computeScrollRemaining(fadeEffectRef));
  }, [fadeEffectRef.current, setScrollRemaining, containerHeight, libraryIndexes]);

  if (libraryIndexes.length > 0) {
    const fadeSize = 48;
    const topFade = Math.min(fadeSize, scrollRemaining.top);
    const bottomFade = Math.min(fadeSize, scrollRemaining.bottom);
    return (
      <Box
        ref={containerRef}
        sx={() => ({
          maxHeight: "50vh",
          width: "100%",
          overflowY: "auto",
          overscrollBehaviorY: "contain",
          maskImage:
            "linear-gradient(to bottom, transparent 0, black var(--top-mask-size, 0), black calc(100% - var(--bottom-mask-size, 0)), transparent 100%);",
          "--top-mask-size": `${scrollRemaining.top > 0 ? `${topFade}px` : "0px"} !important`,
          "--bottom-mask-size": `${
            scrollRemaining.bottom > 0 ? `${bottomFade}px` : "0px"
          } !important`,
        })}
        onScroll={() => setScrollRemaining(computeScrollRemaining(fadeEffectRef))}
      >
        <Stack
          // pl={40}
          style={{ width: "100%" }}
          spacing={0}
        >
          {libraryIndexes.split(",").map((libraryEntry) => (
            <LibraryNav
              key={libraryEntry}
              index={parseInt(libraryEntry, 10)}
              editorActive={editorActive}
              onSelect={onSelect}
            />
          ))}
        </Stack>
      </Box>
    );
  } else {
    return null;
  }
};

const LibraryNav = ({
  index,
  editorActive,
  onSelect,
}: {
  index: number;
  editorActive: boolean;
  onSelect: () => void;
}) => {
  const libraryEntry = useLibraryState((state) => state.entries[index]);
  // const showUnsavedChangesDot = editorActive && unsavedChangesId === libraryEntry.present.id;
  const isEditing = useAppState((state) => state.library.editingEntry === index);
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const reduxDispatch = useAppDispatch();
  return (
    <Button
      key={libraryEntry.present.id}
      compact
      size={"lg"}
      variant={"subtle"}
      fullWidth
      leftIcon={
        <IconPointFilled
          size={20}
          style={{
            visibility: "hidden",
            color: theme.colors.indigo[4],
          }}
        />
      }
      color={"gray"}
      radius={0}
      onClick={() => {
        reduxDispatch(librarySlice.actions.setEditorEntry(index));
        navigate("/editor");
        onSelect();
      }}
      styles={(theme) => ({
        inner: {
          justifyContent: "start",
        },
        root: {
          border: "0px",
          paddingLeft: "12px",
          "&:active": {
            borderStyle: "outset",
            transform: "none ",
          },
          "&:hover": {
            backgroundColor: isEditing && editorActive ? theme.colors.gray[2] : undefined,
            cursor: isEditing && editorActive ? "default" : "pointer",
          },
          fontWeight: isEditing && editorActive ? 800 : "normal",
          color: libraryEntry.present.title ? theme.colors.dark[9] : theme.colors.dark[2],
          backgroundColor: isEditing && editorActive ? theme.colors.gray[2] : undefined,
        },
        leftIcon: {
          marginLeft: "10px",
          marginRight: "2px",
        },
      })}
    >
      <Text
        fz="sm"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {libraryEntry.present.title || `(Untitled)`}
      </Text>
    </Button>
  );
};
